import React, { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress, Menu, MenuItem,
  Table, TableBody, TableCell,
  TableHead,
  TableRow,
  TextField, Typography,
} from '@material-ui/core';
import {
  AddCircleOutline, DeleteOutline, Edit, HighlightOff, MoreVert,
} from '@material-ui/icons';
import Spinner from '@material-ui/core/CircularProgress';
import { SurveyQuestion } from '../../redux/types/survey';
import { beautifyErrors, ValidationErrors } from '../../utils/helpers';
import useStyles from "./styles";
import {
  AnswerSchema,
  getMaxQuestions,
  pickFirstBuilderBlock,
  QuestionSchema
} from "./SurveyValidation";
import CustomButton from '../CustomButton';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  loadSurveyQuestions: () => void;
  updateQuestions: (questions: SurveyQuestion[]) => void;
  surveyQuestions: SurveyQuestion[];
  loading: boolean;
  updating: boolean;
  defaultQuestions: SurveyQuestion[];
  variant: 'postTour' | 'preTour';
};

const SurveyQuestionsComponent: React.FC<Props> = (props) => {
  const {
    loadSurveyQuestions,
    updateQuestions,
    surveyQuestions,
    loading,
    updating,
    defaultQuestions,
    variant,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>(
    defaultQuestions,
  );
  const [selectedQuestion, setSelectedQuestion] = useState<SurveyQuestion>({});
  const [action, setAction] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrors<SurveyQuestion>
  >({});

  const classes = useStyles();
  const open = Boolean(anchorEl);

  const { canEdit } = usePermissions();

  useEffect(() => {
    loadSurveyQuestions();
  }, [loadSurveyQuestions]);

  useEffect(() => {
    const customQuestions = surveyQuestions.map(
      ({
         questionId, type, text, choices,
       }: SurveyQuestion) => ({
        questionId,
        text,
        choices,
        type,
      }),
    );

    if (customQuestions.length === 0) return;

    const maxQuestions = getMaxQuestions(variant);

    if (customQuestions.length <= maxQuestions) {
      setQuestions(surveyQuestions);
      return;
    }

    const firstBlock = pickFirstBuilderBlock(surveyQuestions);
    setQuestions(firstBlock.slice(0, maxQuestions));
  }, [surveyQuestions, variant]);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    question: SurveyQuestion,
  ) => {
    setAnchorEl(event.currentTarget);
    setStep(1);
    setAction(null);
    setSelectedQuestion(question);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setStep(1);
    setAction('edit');
    setAnchorEl(null);
  };

  const handleAddQuestion = () => {
    setSelectedQuestion({});
    setStep(1);
    setAction('add');
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setSelectedQuestion({});
    setStep(1);
    setAction(null);
  };

  const handleUpdate = () => {
    try {
      switch (step) {
        case 1:
          QuestionSchema.validateSync(selectedQuestion, {
            abortEarly: false,
          });

          if (selectedQuestion.type === 2) {
            setStep(2);
            return;
          }

          break;

        case 2:
          AnswerSchema.validateSync(selectedQuestion, {
            abortEarly: false,
          });

          break;

        default:
          break;
      }

      setValidationErrors({});

      const updatedQuestions = questions?.map((question, index) => (index === selectedQuestion?.index ? selectedQuestion : question)
      );

      if (action === 'edit') {
        setQuestions(updatedQuestions);
        updateQuestions(updatedQuestions);
      }

      if (action === 'add') {
        setQuestions([...updatedQuestions, selectedQuestion]);
        updateQuestions([...updatedQuestions, selectedQuestion]);
      }

      setAction(null);
      setStep(1);
    } catch (e) {
      setValidationErrors(beautifyErrors<SurveyQuestion>(e));
    }
  };

  const handleDelete = () => {
    const updatedQuestions = questions.filter(
      (_, index) => index !== selectedQuestion?.index,
    );
    setQuestions(updatedQuestions);
    updateQuestions(updatedQuestions);
    setAnchorEl(null);
  };

  const handleChangeAnswers = (
    e: React.ChangeEvent<HTMLInputElement>,
    choices: string[],
    index: number,
  ) => {
    const newChoices = choices.map((a, idx) => (idx === index ? e.target.value : a));

    setSelectedQuestion({
      ...selectedQuestion,
      choices: newChoices,
    });
  };

  const handleAddLabel = () => {
    if (!selectedQuestion.choices) {
      return;
    }

    setSelectedQuestion({
      ...selectedQuestion,
      choices: [...selectedQuestion.choices, ''],
    });
  };

  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedQuestion({
      ...selectedQuestion,
      text: e.target.value,
    });
  };

  const handleAnswerTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = Number(e.target.value);

    setSelectedQuestion({
      ...selectedQuestion,
      type,
      choices: type === 3 ? ['Yes', 'No'] : new Array(4).fill(''),
    });
  };

  const renderMultipleAnswer = () => {
    const { choices } = selectedQuestion;

    return (
      <Grid container spacing={4}>
        {Array.isArray(choices)
          && choices.map((choice, index) => (
            <Fragment key={index}>
              <Grid item container xs={6} spacing={1} alignItems="center">
                <Grid item xs>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    id={`label_${index + 1}`}
                    name={`label_${index + 1}`}
                    label={`Enter Label ${index + 1}...`}
                    value={choice}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeAnswers(e, choices, index)}
                    helperText={
                      (validationErrors[
                          `choices[${index}]` as keyof typeof validationErrors
                          ]
                        || validationErrors.choices)
                      && 'Fill or delete this field'
                    }
                    error={
                      Boolean(
                        validationErrors[
                          `choices[${index}]` as keyof typeof validationErrors
                          ],
                      ) || Boolean(validationErrors.choices)
                    }
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    disabled={choices.length < 2}
                    onClick={() => {
                      if (choices.length < 2) {
                        return;
                      }

                      const filteredChoices = choices.filter(
                        (el, idx) => index !== idx,
                      );

                      setSelectedQuestion({
                        ...selectedQuestion,
                        choices: filteredChoices,
                      });
                    }}
                  >
                    <HighlightOff
                      color={choices.length < 2 ? 'disabled' : 'error'}
                    />
                  </IconButton>
                </Grid>
              </Grid>
            </Fragment>
          ))}
        {Array.isArray(choices) && choices.length < 4 && (
          <Grid item container xs={6} spacing={1} alignItems="center">
            <Grid item xs>
              <Button
                className={classes.addLabelButton}
                onClick={handleAddLabel}
              >
                Add Label
                <AddCircleOutline
                  className={classes.addButton}
                  style={{ marginLeft: '10px' }}
                />
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };
  const renderAnswers = () => {
    if (action && step === 2 && selectedQuestion?.type === 2) {
      return renderMultipleAnswer();
    }
  };

  return (
    <div className={classes.contentWrapper}>
      <p className={classes.title} style={{ fontSize: '24px' }}>
        {variant === 'preTour' ? 'Select Pre-Tour Survey Question (up to 5)' : 'Select Survey Post-Tour Question (up to 2)' }
      </p>

      {loading ? (
        <LinearProgress style={{ margin: '20px 0', height: '2px' }} />
      ) : (
        <Divider style={{ margin: '20px 0' }} />
      )}

      <div className={classes.tableWrapper}>
        {updating && (
          <div className={classes.spinnerWrapper}>
            <Spinner />
          </div>
        )}
        <Table className={classes.table} aria-label="customized table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell style={{ width: '44px' }} />
              <TableCell>{variant === 'preTour' ? 'Survey Questions' : 'Survey Post-Tour Questions'}</TableCell>
              <TableCell style={{ width: '150px' }}>Answer Type</TableCell>
              <TableCell align="right" style={{ width: '80px' }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading
              && questions.map(({
                                  questionId, text, type, choices,
                                }, index) => (
                <TableRow className={classes.tableRow} key={index}>
                  <TableCell align="center" className={classes.tableCell}>
                    {index + 1}
                  </TableCell>
                  <TableCell align="left" className={classes.tableCell}>
                    {text}
                  </TableCell>
                  <TableCell align="left" className={classes.tableCell}>
                    {type === 2 && 'Multiple Choice'}
                    {type === 3 && 'Yes/No'}
                    {type === 4 && 'Text Stream'}
                    {type === 5 && 'Numbers'}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <div>
                      <IconButton
                        disabled={index === 0 && variant !== 'postTour'}
                        onClick={(e) => {
                          if (!canEdit) {
                            return;
                          }
                          if (index === 0 && variant !== 'postTour') {
                            return;
                          }

                          handleClick(e, {
                            text,
                            type,
                            index,
                            choices,
                            questionId,
                          });
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        id="long-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={open}
                        onClose={handleClose}
                      >
                        <MenuItem onClick={handleEdit}>
                          <Edit
                            color="primary"
                            className={classes.actionButton}
                          />
                          <Typography>Edit</Typography>
                        </MenuItem>
                        <MenuItem onClick={handleDelete}>
                          <DeleteOutline
                            color="error"
                            className={classes.actionButton}
                          />
                          <Typography>Delete</Typography>
                        </MenuItem>
                      </Menu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {((variant === 'preTour' && questions.length < 5) || (variant === 'postTour' && questions.length < 2)) && (
        <p
          className={classes.title}
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '20px',
            marginTop: '20px',
          }}
        >
          Add Custom Questions
          <IconButton onClick={handleAddQuestion} disabled={!canEdit}>
            <AddCircleOutline className={classes.addButton} />
          </IconButton>
        </p>
      )}

      {action && step === 1 && (
        <Grid container spacing={2}>
          <Grid item xs>
            <TextField
              fullWidth
              required
              margin="normal"
              id="survey_question"
              name="survey_question"
              label="Survey Question"
              value={selectedQuestion?.text ?? ''}
              onChange={handleQuestionChange}
              helperText={validationErrors.text && 'Question field is required'}
              error={Boolean(validationErrors.text)}
            />
          </Grid>

          <Grid item xs>
            <TextField
              select
              fullWidth
              required
              margin="normal"
              id="answer_field_type"
              name="answer_field_type"
              label="Answer Field Type"
              value={selectedQuestion?.type ?? ''}
              onChange={handleAnswerTypeChange}
              helperText={
                validationErrors.type && 'Answer type field is required'
              }
              error={Boolean(validationErrors.type)}
            >
              <MenuItem value={5}>Numbers</MenuItem>
              <MenuItem value={3}>Yes/No</MenuItem>
              <MenuItem value={4}>Text Stream (Limit 30 characters)</MenuItem>
              <MenuItem value={2}>Multiple Choice</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      )}

      {action && step === 2 && (
        <>
          <p className={classes.question}>{selectedQuestion.text}</p>
          {renderAnswers()}
        </>
      )}

      <Divider
        style={{
          marginTop: '20px',
          marginBottom: '40px',
        }}
      />

      {action && (
        <Grid
          container
          justify="flex-end"
          spacing={2}
          style={{ marginTop: '32px' }}
        >
          <Grid item>
            <CustomButton variant="white" onClick={handleCancel}>
              Cancel
            </CustomButton>
          </Grid>
          <Grid item>
            <CustomButton
              variant="orange"
              onClick={() => {
                handleUpdate();
              }}
            >
              {selectedQuestion.type === 2
                && action === 'edit'
                && step === 1
                && 'Next'}
              {selectedQuestion.type !== 2
                && action === 'edit'
                && step === 1
                && 'Update'}
              {selectedQuestion.type === 2
                && action === 'add'
                && step === 1
                && 'Next'}
              {selectedQuestion.type !== 2
                && action === 'add'
                && step === 1
                && 'Add'}
              {action === 'edit' && step === 2 && 'Update'}
              {action === 'add' && step === 2 && 'Add'}
            </CustomButton>
          </Grid>
        </Grid>
      )}
    </div>
  );
};
export default React.memo(SurveyQuestionsComponent);
